/* 站点结构化数据：从原 pages.js 中抽取，供路由页面直接消费。
   保留 pages.js 不变，作为兼容入口。 */
import PAGES_CONFIG from './pages';
import { t } from '../utils/i18n';

const findPage = (id) => PAGES_CONFIG.find((p) => p.id === id);
const findChild = (parentId, childId) =>
    findPage(parentId)?.children?.find((c) => c.id === childId);

export const NAV_LINKS = [
    {
        name: t('研究方向', 'Research'),
        path: '/research',
        children: [
            { name: t('大模型', 'Large Models'), path: '/research/llm' },
            { name: t('医疗智能', 'Medical AI'), path: '/research/medical' },
            { name: t('生命科学', 'Life Sciences'), path: '/research/life-science' },
            { name: t('数学智能', 'Mathematical AI'), path: '/research/math' },
            { name: t('数智电力', 'Power Intelligence'), path: '/research/power' },
        ],
    },
    { name: t('动态', 'Updates'), path: '/updates' },
    { name: t('关于我们', 'About Us'), path: '/team' },
];

export const RESEARCH_AREAS = findPage('explore')?.data ?? [];
export const RESEARCH_LLM = findChild('explore', 'llm')?.data ?? {};
export const RESEARCH_MEDICAL = findChild('explore', 'medical-ai')?.data ?? [];
export const RESEARCH_LIFE = findChild('explore', 'life-science')?.data ?? [];
export const RESEARCH_MATH = findChild('explore', 'math')?.data ?? [];
export const RESEARCH_POWER = findChild('explore', 'power')?.data ?? [];

export const UPDATES_BLOG = findChild('updates', 'blog')?.data ?? [];
export const UPDATES_NEWS = findChild('updates', 'news')?.data ?? [];
export const UPDATES_PUBS = findChild('updates', 'publications')?.data ?? [];

export const MISSION = findChild('about', 'mission')?.data ?? {};
export const TEAM = findChild('about', 'team')?.data ?? {};

const JOIN_LINK_FROM_CONFIG = findPage('about')?.children?.find((c) => c.id === 'join');
export const JOIN_US = {
    href: JOIN_LINK_FROM_CONFIG?.href ?? '',
    target: JOIN_LINK_FROM_CONFIG?.target ?? '_blank',
};

export const SITE_STATS = [
    { value: '5', label: t('核心研究方向', 'Core Research Areas'), sub: t('截至 2026.07', 'As of Jul 2026'), iconName: 'atom' },
    { value: '20+', label: t('发表论文', 'Publications'), sub: t('已发表 / 在投', 'Published / Under Review'), iconName: 'fileText' },
    { value: '50+', label: t('研究人员', 'Researchers'), sub: t('在岗研究员', 'Active Research Staff'), iconName: 'users' },
    { value: '10+', label: t('合作机构', 'Partner Institutions'), sub: t('高校 / 科研院所', 'Universities / Research Institutes'), iconName: 'link2' },
];

/* ============ 研究方向跨页面共享配置 (PA-4) ============ */
export const RESEARCH_ROUTE_MAP = {
    llm: '/research/llm',
    'medical-ai': '/research/medical',
    'life-science': '/research/life-science',
    math: '/research/math',
    power: '/research/power',
};

export const AREA_TAGS = {
    llm: ['Code-Flow', t('Loop 架构', 'Loop Architecture'), 'Thinking · Instruct'],
    'medical-ai': ['Fleming-R1', t('多模态', 'Multimodal'), t('可解释推理', 'Explainable Reasoning')],
    'life-science': [t('UBio-MolFM', 'Ubio-MolFM'), t('分子模拟', 'Molecular Simulation')],
    math: ['Autoformalizer', 'Lean4', t('形式化数学', 'Formal Mathematics')],
    power: [t('功率预测', 'Power Forecasting'), t('储能优化', 'Storage Optimization'), t('柔性负荷', 'Flexible Load')],
};

/* PA-5: 4 色 tag —— 蓝 / 琥 / 绿 / 紫，互相辨识 */
export const AREA_TAG_COLOR = {
    llm: 'tag-blue',
    'medical-ai': 'tag-amber',
    'life-science': 'tag-green',
    math: 'tag-purple',
    power: 'tag-orange',
};

export const AREA_KPI = {
    llm: t('3 尺寸 · 3 形态 · 已开源', '3 model sizes · 3 versions · Open-sourced'),
    'medical-ai': t('4 产品矩阵 · 临床落地中', '4 products · In clinical deployment'),
    'life-science': t('2 子方向 · 持续迭代', '2 sub\u2011areas · Continuous iteration'),
    math: t('与北大合作 · Lean4 验证', 'In collaboration with Peking University · Lean4 verification'),
    power: t('4 场景落地 · 已运营', '4 scenarios deployed · In operation'),
};

/* PA-4: MathPage 3 张卡的 KPI 行（与卡的标题顺序对齐） */
export const MATH_CARD_KPI = [
    t('Anderson 猜想 · Lean4 验证 19,000 行', "Anderson's Conjecture · Lean4 verification \u2014 19,000 lines"),
    t('自然语言 → Lean', 'Natural Language \u2192 Lean'),
    t('SOTA 级引理检索 · 自然语言 + Lean', 'SOTA lemma retrieval · Natural Language + Lean'),
];

/* PA-7: Updates tag 文案 → domain → 颜色映射
   Round 3 MUST-FIX 2：
   - coder / llm / 大模型  → llm   (蓝)
   - medical / 医疗        → medical (琥)
   - life / 生科 / 生命科学 → life  (绿)
   - math / 数学智能            → math  (紫)
   - 公司动态 / 合作 / 活动 / 招聘 / 论文 / 报告 / 书籍 → neutral */
export const UPDATES_TAG_DOMAIN = {
    coder: 'llm',
    llm: 'llm',
    '大模型': 'llm',
    'Large Models': 'llm',
    math: 'math',
    '数学智能': 'math',
    'Mathematics': 'math',
    'Mathematical AI': 'math',
    medical: 'medical',
    '医疗': 'medical',
    '医疗智能': 'medical',
    'Medical AI': 'medical',
    life: 'life',
    '生科': 'life',
    '生命科学': 'life',
    'Life Sciences': 'life',
    power: 'power',
    '电力': 'power',
    '数智电力': 'power',
    'Power Intelligence': 'power',
    '公司动态': 'neutral',
    '合作': 'neutral',
    '活动': 'neutral',
    '招聘': 'neutral',
    '论文': 'neutral',
    '报告': 'neutral',
    '书籍': 'neutral',
};

export const DOMAIN_TAG_COLOR = {
    llm: 'tag-blue',
    medical: 'tag-amber',
    life: 'tag-green',
    math: 'tag-purple',
    power: 'tag-orange',
    neutral: 'tag-neutral',
};

export const DOMAIN_FILTERS = [
    { id: 'all', label: t('全部域', 'All') },
    { id: 'llm', label: t('大模型', 'Large Models') },
    { id: 'medical', label: t('医疗智能', 'Medical AI') },
    { id: 'life', label: t('生命科学', 'Life Sciences') },
    { id: 'math', label: t('数学智能', 'Mathematical AI') },
    { id: 'power', label: t('数智电力', 'Power Intelligence') },
];

export function resolveTagDomain(tag) {
    if (!tag) return 'neutral';
    return UPDATES_TAG_DOMAIN[tag] || 'neutral';
}

/* ============================================================
   Hero 首屏「实时动态面板」— 动态派生自 UPDATES_NEWS / PUBS
   - News: 按 date 降序取前 2 条
   - Publications: 按 date 降序取前 2 条
   - 共 4 条
   ============================================================ */

/** 将 'YYYY-MM-DD' 格式化为 'Mon DD'，无 date 时返回 fallback */
function formatDateLabel(dateStr, fallback) {
    if (!dateStr) return fallback;
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const parts = dateStr.split('-');
    if (parts.length < 3) return fallback;
    const month = months[parseInt(parts[1], 10) - 1] || '';
    const day = parseInt(parts[2], 10);
    return `${month} ${day}`;
}

function deriveLatestActivities(items, opts, count = 2) {
    if (!items || items.length === 0) return [];
    const sorted = opts.sortByDate
        ? [...items].sort((a, b) => (b.date || '').localeCompare(a.date || ''))
        : items;
    return sorted.slice(0, count).map((item, i) => ({
        id: `${opts.id}-${i}`,
        dateLabel: formatDateLabel(item.date, opts.fallbackLabel),
        dateFull: item.date || '',
        statusIcon: opts.statusIcon,
        tagLabel: item.tag,
        tagColor: DOMAIN_TAG_COLOR[resolveTagDomain(item.tag)],
        title: item.title,
        ...(item.href ? { href: item.href } : {}),
        ...(item.id && !item.href ? { articleId: item.id } : {}),
    }));
}

export const LATEST_ACTIVITIES = [
    ...deriveLatestActivities(UPDATES_NEWS, {
        id: 'latest-news',
        statusIcon: 'rocket',
        fallbackLabel: t('新闻', 'News'),
        sortByDate: true,
    }, 2),
    ...deriveLatestActivities(UPDATES_PUBS, {
        id: 'latest-pub',
        statusIcon: 'fileText',
        fallbackLabel: t('出版', 'Publication'),
        sortByDate: true,
    }, 2),
].filter(Boolean);

export const HOME_VALUES = [
    {
        title: t('前沿技术突破', 'Frontier Tech Breakthroughs'),
        desc: t(
            '聚焦原创性底层算法与下一代 AI 架构，从理论到工程，持续打开能力边界。',
            'Rooted in proprietary foundational algorithms and next-generation AI architecture, continuously pushing capability boundaries from theory to engineering.'
        ),
    },
    {
        title: t('高复杂度场景落地', 'High\u2011Complexity Real\u2011World Deployment'),
        desc: t(
            '以产业一线核心瓶颈锚定研究方向，让技术在真实业务中接受最严苛的检验。',
            'Anchoring research to frontline industry bottlenecks, putting technology through the most rigorous real-world validation.'
        ),
    },
    {
        title: t('产学研生态协同', 'Global Academia\u2013Industry Collaboration'),
        desc: t(
            '深耕全球科研合作网络，与顶尖院校、龙头企业共建可持续的创新闭环。',
            'Cultivating a global research collaboration network, co-building a sustainable innovation loop with top universities and leading enterprises.'
        ),
    },
];
